// <s-recently-viewed>: stores viewed product URLs in localStorage and renders cards from a Liquid <template>.
// Product data comes from each product's own `.js` endpoint, built from URLs Liquid wrote (no hardcoded routes).
const STORAGE_KEY = 'grecia:recently-viewed';
const MAX_STORED = 8;

class SRecentlyViewed extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const current = this.dataset.currentUrl;
    const limit = Number(this.dataset.limit || 4);

    let stored = [];
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) stored = [];
    } catch {
      stored = [];
    }

    const others = stored.filter((url) => typeof url === 'string' && url !== current).slice(0, limit);
    const next = [current, ...stored.filter((url) => url !== current)].slice(0, MAX_STORED);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage can be unavailable (private mode); the section simply stays hidden.
    }

    if (others.length) this.#render(others);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #render(urls) {
    const list = this.querySelector('[data-recent-list]');
    const template = this.querySelector('template[data-recent-card]');
    if (!list || !template) return;

    const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    const results = await Promise.all(urls.map(async (url) => {
      try {
        const response = await fetch(`${url.split('?')[0]}.js`, { signal: this.#ac.signal });
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    }));

    const products = results.filter((product) => product && product.available !== undefined);
    if (!products.length) return;

    for (const product of products) {
      const node = template.content.cloneNode(true);
      const url = product.url;
      node.querySelectorAll('[data-recent-link]').forEach((link) => {
        link.href = url;
        link.setAttribute('aria-label', product.title);
      });
      const img = node.querySelector('[data-recent-image]');
      if (img && product.featured_image) {
        const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
        img.src = `${src}${src.includes('?') ? '&' : '?'}width=600`;
        img.alt = product.title;
      }
      const title = node.querySelector('[data-recent-title]');
      if (title) title.textContent = product.title;
      const price = node.querySelector('[data-recent-price]');
      if (price) price.textContent = formatter.format(product.price / 100);
      list.append(node);
    }

    this.hidden = false;
  }
}

if (!customElements.get('s-recently-viewed')) customElements.define('s-recently-viewed', SRecentlyViewed);
